import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from './Button';
import { ChatProductCard } from './ChatProductCard';
import { useCart } from '../context/CartContext';

function useBubbleTheme() {
  const [isLight, setIsLight] = useState(false);

  useEffect(() => {
    const check = () => {
      // Approximate Y position of the bubble in the document
      const bubbleDocY = window.scrollY + window.innerHeight - 100;

      const darkSections = document.querySelectorAll('[data-bubble-theme="light"]');
      let light = false;

      darkSections.forEach(el => {
        const top = el.getBoundingClientRect().top + window.scrollY;
        const bottom = top + el.offsetHeight;
        if (bubbleDocY >= top && bubbleDocY <= bottom) {
          light = true;
        }
      });

      setIsLight(light);
    };

    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);

  return { isLight };
}

export function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const { isLight, bubbleRef } = useBubbleTheme();
  const { isCartOpen } = useCart();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! 👋 I am Cerebra, your wellness assistant. How can I help you today? Feel free to ask about our products, ingredients, or anything else!'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Use Supabase Edge Function for Berleto Chat with anon key
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imdld3Zva2x4YWFxZGZ0dXJnYXBqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ4NzM3MDcsImV4cCI6MjA2MDQ0OTcwN30.27mX9GlWs3li-V4cT3Imja67codxYYBm5JgHRyeKO84';
      
      const response = await fetch('https://gewvoklxaaqdfturgapj.supabase.co/functions/v1/berleto-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          messages: [...messages, { role: 'user', content: userMessage }]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      let data;
      
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse JSON:', text);
        throw new Error('Invalid response from server');
      }
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Add assistant response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.message,
        products: data.products // Include product recommendations
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I am having trouble connecting right now. Please try again in a moment or contact our support team directly.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    "What's the difference between Rush and Synaptiq?",
    "Are your products vegan?",
    "How should I take Vitra?",
    "Do you offer subscriptions?"
  ];

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && !isCartOpen && (
          <motion.button
            ref={bubbleRef}
            initial={{ scale: 0, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: 16 }}
            transition={{ type: 'spring', damping: 20, stiffness: 260 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-8 right-4 md:bottom-10 md:right-7 z-50 flex items-center gap-2 pl-2.5 pr-3 py-2.5 rounded-full"
            style={{
              background: isLight ? '#E8DCC4' : '#1A2A1A',
              boxShadow: isLight
                ? '0 4px 24px rgba(232,220,196,0.25), 0 0 0 1px rgba(26,42,26,0.12)'
                : '0 4px 24px rgba(26,42,26,0.35), 0 0 0 1px rgba(232,220,196,0.1)',
              transition: 'background 0.4s ease, box-shadow 0.4s ease',
            }}
          >
            {/* Icon circle */}
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: '#B8704D',
                transition: 'background 0.4s ease',
              }}
            >
              <MessageCircle className="w-3 h-3 text-white" />
            </div>

            {/* Label */}
            <span
              className="text-xs font-medium hidden md:block"
              style={{
                color: isLight ? '#1A2A1A' : '#FAF9F6',
                letterSpacing: '0.01em',
                transition: 'color 0.4s ease',
              }}
            >
              Ask Cerebra
            </span>

            {/* Pulse dot */}
            <span className="relative flex h-1.5 w-1.5 ml-0.5">
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                style={{ background: '#4A7C59' }}
              />
              <span
                className="relative inline-flex rounded-full h-1.5 w-1.5"
                style={{ background: '#4A7C59' }}
              />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop for mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40 md:hidden"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Chat Container */}
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.8 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-4 right-4 left-4 md:left-auto z-50"
            style={{ maxWidth: '380px', marginLeft: 'auto' }}
          >
              <div
                className="overflow-hidden border flex flex-col"
                style={{
                  height: '500px',
                  maxHeight: 'calc(100vh - 2rem)',
                  background: '#FAF9F6',
                  borderColor: 'rgba(26,42,26,0.12)',
                  boxShadow: '0 18px 60px rgba(26,42,26,0.18)',
                }}
              >
              {/* Header */}
              <div
                className="p-4 flex items-center justify-between flex-shrink-0 border-b"
                style={{ borderColor: 'rgba(26,42,26,0.08)' }}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="relative">
                    <div
                      className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center p-1 border"
                      style={{ borderColor: 'rgba(26,42,26,0.1)', background: '#F0EDE6' }}
                    >
                      <img 
                        src="/logo/berleto-logo-dark.png" 
                        alt="Berleto" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-2.5 h-2.5 border"
                        style={{ background: '#4A7C59', borderColor: '#FAF9F6' }}
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm sm:text-base" style={{ color: '#1A2A1A' }}>Cerebra</h3>
                    <p className="text-[10px] sm:text-xs tracking-[0.14em] uppercase" style={{ color: 'rgba(26,42,26,0.42)' }}>
                      Berleto support
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 transition-colors border"
                  style={{ borderColor: 'rgba(26,42,26,0.1)', color: 'rgba(26,42,26,0.58)', background: 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F0EDE6'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4" style={{ background: '#FAF9F6' }}>
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'} gap-2 items-start`}
                  >
                    {/* Avatar for assistant messages */}
                    {message.role === 'assistant' && (
                      <div
                        className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center p-1 border"
                        style={{ background: '#F0EDE6', borderColor: 'rgba(26,42,26,0.08)' }}
                      >
                        <img 
                          src="/logo/berleto-logo-dark.png" 
                          alt="Berleto"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    
                    <div className="flex flex-col max-w-[85%] sm:max-w-[80%]">
                      <div
                        className="p-3 border"
                        style={{
                          background: message.role === 'user' ? '#1A2A1A' : '#F0EDE6',
                          borderColor: message.role === 'user' ? '#1A2A1A' : 'rgba(26,42,26,0.08)',
                          color: message.role === 'user' ? '#FAF9F6' : '#1A2A1A',
                        }}
                      >
                        <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      </div>
                      
                      {/* Show product cards if available */}
                      {message.products && message.products.length > 0 && (
                        <div className="mt-2 space-y-2 w-full">
                          {message.products.map((product, idx) => (
                            <ChatProductCard key={idx} product={product} />
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="border p-3" style={{ background: '#F0EDE6', borderColor: 'rgba(26,42,26,0.08)' }}>
                      <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#B8704D' }} />
                    </div>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Questions */}
              {messages.length === 1 && (
                <div
                  className="px-3 sm:px-4 py-3 border-t flex-shrink-0"
                  style={{ background: '#FAF9F6', borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <p className="text-[10px] sm:text-xs tracking-[0.16em] uppercase mb-2" style={{ color: 'rgba(26,42,26,0.42)' }}>
                    Quick questions
                  </p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {quickQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setInput(question);
                          inputRef.current?.focus();
                        }}
                        className="text-[10px] sm:text-xs px-2.5 sm:px-3 py-1.5 transition-colors border"
                        style={{ borderColor: 'rgba(26,42,26,0.1)', color: '#1A2A1A', background: 'transparent' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F0EDE6'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <form
                onSubmit={sendMessage}
                className="p-3 sm:p-4 border-t flex-shrink-0"
                style={{ background: '#FAF9F6', borderColor: 'rgba(26,42,26,0.08)' }}
              >
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask me anything..."
                    disabled={isLoading}
                    className="flex-1 px-3 sm:px-4 py-2.5 border bg-transparent focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontSize: '16px', borderColor: 'rgba(26,42,26,0.14)', color: '#1A2A1A' }}
                  />
                  <Button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="disabled:opacity-50 disabled:cursor-not-allowed p-2 w-10 h-10 flex items-center justify-center"
                    style={{ background: '#1A2A1A', color: '#FAF9F6' }}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
